import React, {useEffect, useRef, useState, useLayoutEffect} from 'react';
import styled from 'styled-components';
import {MediumTitle, screenPadding, centeredFlex, fontSize, primaryGreenBtn, wh} from '../../mixins';
import {useSelector, useDispatch} from 'react-redux';
import CardIcons from './CardIcons';
import {loadStripe} from '@stripe/stripe-js';
import {PaymentRequestButtonElement, Elements} from '@stripe/react-stripe-js';
import {
    selectCardName,
    selectFormValidity,
    selectPlan,
    selectSuccess,
    sendMainPurchaseRequest,
    paypalPurchase,
} from './paywallSlice';
import {
    selectToken,
    selectUserID,
    selectStripePublishKey,
    selectStripeAccountName,
    selectBtToken,
} from '../auth/authSlice';
import Stripe from '../stripe/Stripe';
import SuccessScreen from './SuccessScreen';
import {ArrowLeft} from '../../UI/Icons';
import {selectEventsData, selectFBPurchaseData, selectOfferType, setEventsData} from '../onboarding/onboardingSlice';
import {sendAnalyticsEvents} from '../../analytics/common';
import {EVENTS} from '../../analytics/events';
import {selectUserInfo} from '../login/loginSlice';
import braintree from 'braintree-web';
import {Paypal} from './icons';
import {setCheckoutSpinner} from '../spinner/spinnerSlice';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';


const StyledBackButton = styled.button`
    border: none;
    background-color: transparent;
    width: 25px;
    height: auto;
    padding: 4px;
    position: absolute;
    top: 40px;
    left: 5%;
    cursor: pointer;
`;

const StyledContainer = styled.div`
  ${centeredFlex};
  ${screenPadding};
  max-width: 380px;
  min-height: 100%;
  position: relative;
  
   @media screen and (min-width: 768px) {
      height: calc(100vh - 40px);
      min-height: unset;
  }
`;

const Title = styled.h1`
  ${MediumTitle}
`;

const StyledTerms = styled.p`
  font-weight: 400;
  ${fontSize(12)};
  line-height: 16px;
  text-align: center;
  color: #FFFFFF;
`;

const StyledCard = styled.div`
  width: 100%;
  min-height: 225px;
  margin: 40px auto 24px;
`;

const StyledButton = styled.button`
  ${primaryGreenBtn};
  background-color: #FBA253;
  width: 327px;
  border: none;
`;
const PaymentRequestContainer = styled.div`
  width: 100%;
  max-width: 327px;
  div iframe {
    border-radius: 24px;
  }
`;
const PayWith = styled.div`
  margin: 24px 0 8px;
  font-weight: 500;
  ${fontSize(16)};
  line-height: 22px;
  text-align: center;
  color: #ffffff;
`;

const PaypalButton = styled.div`
    width: 90%;
    ${wh('90%','46px')}
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background: #F0C348;
    border-radius: 24px;
    cursor: pointer;
`;

const AlternativePaymentButtons = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
`;

const style = {
    base: {
        color: '#00A08B',
        lineHeight: '24px',
        fontWeight: 300,
        fontFamily: 'Open Sans, Segoe UI, sans-serif',
        fontSize: '18px',
        height: '28px',

        '::placeholder': {
            color: '#ADADAD',
            letterSpacing: '1.2px',
            fontSize: '18px',
            lineHeight: '24px',
        },
    },
};

const isStage = process.env.REACT_APP_ENV === 'stage';

const CheckoutScreen = ({onBackClick}) => {
    const [load, setLoad] = useState(false);
    const [eventSent, setEventSent] = useState(false);
    const dispatch = useDispatch();
    const name = useSelector(selectCardName);
    const plan = useSelector(selectPlan);
    const token = useSelector(selectToken);
    const stripePk = useSelector(selectStripePublishKey);
    const stripeAccountName = useSelector(selectStripeAccountName);
    const [showPaypal, setShowPaypal] = useState(false);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    const amplitudeEventData = useSelector(selectEventsData);
    const fbPurchaseData = useSelector(selectFBPurchaseData);
    const userID = useSelector(selectUserID);
    const userInfo = useSelector(selectUserInfo);
    const isFormValid = useSelector(selectFormValidity);
    const isSuccess = useSelector(selectSuccess);
    const stripe = useRef();
    const card = useRef();
    const expiration = useRef();
    const cvc = useRef();
    const gzUserId = userID || userInfo.id;
    const paypal = useRef();
    const btToken = useSelector(selectBtToken);
    const offerType = useSelector(selectOfferType);
    useEffect(() => {
        setLoad(false);
        setStripePromise(null);
        setTimeout(() => setStripePromise(loadStripe(stripePk)), 300);
    }, [stripePk]);

    useEffect(() => {
        if (stripePromise) {
            stripePromise.then((stripeObj) => {
                const elements = stripeObj.elements();

                card.current = elements.create('cardNumber', {style: style});
                expiration.current = elements.create('cardExpiry', {style: style});
                cvc.current = elements.create('cardCvc', {style: style, placeholder: '***',});

                stripe.current = stripeObj;
                setLoad(true);
            });
        }
    }, [stripePromise]);

    useLayoutEffect(() => {
        if (stripe.current) {
            const pr = stripe.current.paymentRequest({
                country: 'US',
                currency: 'usd',
                total: {
                    label: `Geozilla ${amplitudeEventData?.description} plan`,
                    amount: Math.round(amplitudeEventData?.periodPrice * 100),
                },
                displayItems: [
                    {
                        label: `Geozilla ${amplitudeEventData?.description} plan`,
                        amount: Math.round(amplitudeEventData?.periodPrice * 100),
                    },
                ],
                requestPayerName: true,
                requestPayerEmail: true,
            });

            pr.canMakePayment().then((result) => {
                if (result && amplitudeEventData) {
                    setPaymentRequest(pr);
                }
            });

            pr.on('paymentmethod', async (e) => {
                const successPayment = () => e.complete('success');
                const failedPayment = () => e.complete('fail');

                onPurchaseClick(e, successPayment, failedPayment);
            });
        }

    }, [stripe.current, amplitudeEventData]);

    const sendSupBuyTapped = localData => {
        dispatch(setEventsData(localData));
        sendAnalyticsEvents(EVENTS.subBuyTapped, {
            ...amplitudeEventData,
            ...localData
        });
    };

    const initializeBraintree = token => {
        braintree.client.create({
            authorization: token
        }, function (clientErr, clientInstance) {
            if (clientErr) {
                console.error('Error creating client:', clientErr);
                return;
            }
            braintree.paypal.create({
                client: clientInstance
            }, function (paypalErr, paypalInstance) {
                if (paypalErr) {
                    console.error('Error creating PayPal:', paypalErr);
                    return;
                }

                paypal.current = paypalInstance;
                setShowPaypal(
                    amplitudeEventData?.stripeSettings?.availablePayments?.braintree.includes('paypal') &&
                        (offerType === 'fullPrice' || offerType === 'up')
                );
            });
        });
    };
    useEffect(() => {
        btToken && initializeBraintree(btToken);
    }, [btToken]);

    const onPayPalClick = () => {
        dispatch(setCheckoutSpinner({show: true, type: 'checkout'}));
        paypal.current.tokenize({
            flow: 'vault',
            currency: 'USD',
        }, function (tokenizeErr, payload) {
            if (tokenizeErr) {
                if (tokenizeErr.type !== 'CUSTOMER') {
                    console.error('Error tokenizing:', tokenizeErr);
                }
                dispatch(setCheckoutSpinner({show: false, type: ''}));
                return;
            }
            const localEventsData = {
                payment_method: 'Paypal',
                stripe_account_name: 'n/a',
                'Payments gateways': 'Braintree'
            };
            const extendedEventsData = {
                ...amplitudeEventData,
                ...localEventsData,
            };
            setAmplitudeUserProperties({ 'Payments gateways': 'Braintree' });
            sendSupBuyTapped(localEventsData);
            dispatch(paypalPurchase(payload.nonce, plan, token, fbPurchaseData, extendedEventsData, gzUserId));
        });
    };

    const onPurchaseClick = (paymentMethod = null, successPayment = null, failedPayment = null) => {
        const paymentMethodName = paymentMethod?.walletName || 'Stripe';
        const paymentCardBrandName = paymentMethod?.card?.brand || 'visa';
        const paymentMethodObj = paymentMethod ? {methodName: paymentMethodName, stripeNonce: paymentMethod.paymentMethod.id} : null;
        let localEventsData = {
            payment_method: paymentMethodName,
            card_brand: paymentCardBrandName,
            cardBrand: paymentCardBrandName,
            stripe_account_name: stripeAccountName,
            'Payments gateways': `Stripe [${stripeAccountName}]`
        };
        const extendedEventsData = {
            ...amplitudeEventData,
            ...localEventsData,
        };
        setAmplitudeUserProperties({
            'Payments gateways': `Stripe [${stripeAccountName}]`,
        });
        sendSupBuyTapped(localEventsData);
        dispatch(sendMainPurchaseRequest(paymentMethodObj, stripe.current, card.current, name, plan, token, fbPurchaseData, extendedEventsData, gzUserId, successPayment, failedPayment));
    };

    useEffect(() => {
        if (amplitudeEventData.price && !eventSent) {
            sendAnalyticsEvents(EVENTS.checkoutScreenShown, amplitudeEventData);
            setEventSent(true);
        }
    }, [amplitudeEventData]);

    const onBackClickEvent = () => sendAnalyticsEvents(EVENTS.subBuyClosed);

    return (
        <StyledContainer>
            {isSuccess ?
                <SuccessScreen/>
                : (
                    <>
                        <StyledBackButton onClick={() => {onBackClick();onBackClickEvent();}} id='come-back-button'>{ArrowLeft}</StyledBackButton>

                        <Title>Checkout</Title>
                        <StyledCard>
                            {load && <Stripe card={card.current} expiration={expiration.current} cvc={cvc.current}/>}
                        </StyledCard>
                        <CardIcons/>
                        <StyledButton disabled={!isFormValid} onClick={() => onPurchaseClick(null)} id='continue-button'>Confirm Payment</StyledButton>
                        {showPaypal && (
                            <AlternativePaymentButtons>
                                <PayWith>Or Checkout With</PayWith>
                                <PaypalButton id={'paypal-pay-button'} onClick={onPayPalClick}>{Paypal}</PaypalButton>
                            </AlternativePaymentButtons>
                        )}

                        {paymentRequest && (
                            <Elements stripe={stripePromise}>
                                <AlternativePaymentButtons>
                                    <PayWith>Or Checkout With</PayWith>
                                    <PaymentRequestContainer>
                                        <PaymentRequestButtonElement
                                            options={{
                                                paymentRequest,
                                                id: 'apple-google-pay-button',
                                                style: {
                                                    paymentRequestButton: {
                                                        height: '42px',
                                                        theme: 'light',
                                                        type: 'default',
                                                    },
                                                },
                                            }}
                                        />
                                    </PaymentRequestContainer>
                                </AlternativePaymentButtons>
                            </Elements>
                        )}
                        <StyledTerms>30-day money-back guarantee.</StyledTerms>
                    </>
                )
            }
            {isStage && (
                <input
                    type={'hidden'}
                    id={'test-skip-checkout'}
                />
            )}
        </StyledContainer>
    );
};


export default CheckoutScreen;
